import Controller from '@ember/controller';
import { alias } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import { action, computed } from '@ember/object';
import FileSaver from 'file-saver';
import { LSG_CHECKBOX_TERNARY__STATES } from 'sh-www/components/lsg/lsg-checkbox-ternary';
import { makeMoney } from 'sh-www/helpers/make-money';
import { PAYIN__COLUMN_HEADERS } from 'sh-www/constants/routes/admin/accounting/payin';
import { BILLABLE_RECORD } from 'sh-www/models/billable-record';

class AdminAccountingPayinController extends Controller {
  /**
   * Register services
   */
  @service('state-managers/application') applicationStateManager;
  @service constants;
  @service moment;
  @service('states/routes/admin/accounting/payin') state;
  @service('state-managers/routes/admin/accounting/payin') stateManager;
  @service store;
  @service('state-manager-hybrids/heartbeat-state-manager-hybrid') heartbeatStateManager;

  id = undefined;
  applicationId = undefined;
  companyId = undefined;

  final_status = null;
  sent_status = null;
  unpaid = null;
  overpaid = null;
  exact = null;
  partial = null;
  stripe = null;
  invoice = null;
  revised_status = null;

  /**
   * @type {Number}     How many records to load.
   */
  count = 10;

  //Multi-select params
  companyOptions = [];
  selectedCompany = [];
  companyFilter = {
    name: null
  };

  @alias('state.paramBatch')
  _selectedBatchFilter;

  /**
   * This is a string that will be set with which search category has been picked by the user.
   *
   * @type {id|entityName|applicationId}
   */
  _selectedSearchCategory = '';

  /**
   * ------------------ NOTE ----------------------------
   * All these params are ALIASED to the top level state. Easiest thing to do for now to not have
   * to rewrite this entire controller.
   */
  @alias('state.paramApplicationId')
  _applicationId;

  @alias('state.paramCompanyId')
  _companyId;

  @alias('state.paramInvoiceId')
  _id;

  @alias('state.paramSort')
  sort;

  @computed('_selectedBatchFilter')
  get batchAllChecked() {
    return this._selectedBatchFilter === this.state.BATCH_ALL
      ? 'checked'
      : '';
  }

  @computed('_selectedBatchFilter')
  get batchHideChildrenChecked() {
    return this._selectedBatchFilter === this.state.BATCH_HIDE_CHILDREN
      ? 'checked'
      : '';
  }

  @computed('_selectedBatchFilter')
  get batchUnbatchedChecked() {
    return this._selectedBatchFilter === this.state.BATCH_UNBATCHED
      ? 'checked'
      : '';
  }

  //Initial checked status of the search inputs
  @computed('_selectedSearchCategory')
  get entityNameChecked() {
    return this._selectedSearchCategory === 'entityName'
      ? 'checked'
      : '';
  }

  @computed('_selectedSearchCategory')
  get applicationIdChecked() {
    return this._selectedSearchCategory === 'applicationId'
      ? 'checked'
      : '';
  }

  @computed('_selectedSearchCategory')
  get idChecked() {
    return this._selectedSearchCategory === 'id'
      ? 'checked'
      : '';
  }

  /**
   * All the states of each checkbox for the input form.
   *
   * @type {String}     of type LSG_CHECKBOX_TERNARY__STATES.*
   */
  @computed('state.paramApproved')
  get _checkboxStateApproved() {
    return this._getCheckboxStatusForValue(this.state.paramApproved);
  }

  @computed('state.paramAgreement')
  get _checkboxStateAgreement() {
    return this._getCheckboxStatusForValue(this.state.paramAgreement);
  }

  @computed('state.paramRevisedStatus')
  get _checkboxStateRevisedStatus() {
    return this._getCheckboxStatusForValue(this.state.paramRevisedStatus);
  }

  @computed('state.paramInvoice')
  get _checkboxStateInvoice() {
    return this._getCheckboxStatusForValue(this.state.paramInvoice);
  }

  @computed('state.paramStripe')
  get _checkboxStateStripe() {
    return this._getCheckboxStatusForValue(this.state.paramStripe);
  }

  @computed('state.paramPartial')
  get _checkboxStatePartial() {
    return this._getCheckboxStatusForValue(this.state.paramPartial);
  }

  @computed('state.paramExact')
  get _checkboxStateExact() {
    return this._getCheckboxStatusForValue(this.state.paramExact);
  }

  @computed('state.paramOverpaid')
  get _checkboxStateOverpaid() {
    return this._getCheckboxStatusForValue(this.state.paramOverpaid);
  }

  @computed('state.paramUnpaid')
  get _checkboxStateUnpaid() {
    return this._getCheckboxStatusForValue(this.state.paramUnpaid);
  }

  @computed('state.paramFinalStatus')
  get _checkboxStateFinalStatus() {
    return this._getCheckboxStatusForValue(this.state.paramFinalStatus);
  }

  @computed('state.paramSentStatus')
  get _checkboxStateSentStatus() {
    return this._getCheckboxStatusForValue(this.state.paramSentStatus);
  }

  @computed('heartbeatStateManager.adminBag.billable_needs_approval_count')
  get _needsApprovalCount() {
    return this.heartbeatStateManager.adminBag?.billable_needs_approval_count ?? 0;
  }

  @computed('_needsApprovalCount')
  get _needsApprovalButtonText() {
    const needsApprovalCount = this._needsApprovalCount;
    return `Needs Approval (${needsApprovalCount})`;
  }

  @computed('state.queryParams')
  get _hasAnyFilters() {
    const { queryParams } = this.state;
    const filterCount = Object.keys(queryParams).reduce((count, key) => {
      if (
        queryParams[key] === undefined
        || queryParams[key] === null
        || queryParams[key] === ''
      ) {
        return count;
      }
      return count + 1;
    }, 0);

    return filterCount > 0;
  }

  /**
   * Get a new filter object for the server.
   *
   * @param {Object} queryParams
   * @return {Object}
   */
  getNewFilter(queryParams) {
    let filter = {};

    if (queryParams.approved) {
      filter['approved'] = queryParams.approved;
    }

    if (queryParams.agreement) {
      filter['agreement'] = BILLABLE_RECORD.AGREEMENT.AGREED;
    }

    if (queryParams.batch) {
      if (queryParams.batch === this.state.BATCH_HIDE_CHILDREN) {
        filter['is_child'] = '0';
      } else if (queryParams.batch === this.state.BATCH_UNBATCHED) {
        filter['is_child'] = '0';
        filter['is_parent'] = '0';
      }
    }

    if (queryParams.applicationId) {
      filter['applicationId'] = queryParams.applicationId;
    }

    if (queryParams.id) {
      filter['id'] = queryParams.id;
    }

    if (queryParams.companyId) {
      filter['companyId'] = queryParams.companyId;
    }

    if (queryParams.final_status) {
      filter['final_status'] = queryParams.final_status;
    }

    if (queryParams.sent_status) {
      filter['sent_status'] = queryParams.sent_status;
    }

    let initPaidStatus = () => {
      if (filter['paid_status']) {
        return;
      }

      filter['paid_status'] = {};
    };

    if (queryParams.unpaid) {
      initPaidStatus();
      filter['paid_status']['unpaid'] = queryParams.unpaid;
    }

    if (queryParams.overpaid) {
      initPaidStatus();
      filter['paid_status']['overpaid'] = queryParams.overpaid;
    }

    if (queryParams.exact) {
      initPaidStatus();
      filter['paid_status']['exact'] = queryParams.exact;
    }

    if (queryParams.partial) {
      initPaidStatus();
      filter['paid_status']['partial'] = queryParams.partial;
    }

    if (queryParams.exact) {
      initPaidStatus();
      filter['paid_status']['exact'] = queryParams.exact;
    }


    let initPaymentMethod = () => {
      if (filter['payment_method']) {
        return;
      }

      filter['payment_method'] = {};
    };

    if (queryParams.stripe) {
      initPaymentMethod();
      filter['payment_method']['stripe'] = queryParams.stripe;
    }

    if (queryParams.invoice) {
      initPaymentMethod();
      filter['payment_method']['invoice'] = queryParams.invoice;
    }

    if (queryParams.revised_status) {
      filter['revised_status'] = queryParams.revised_status;
    }

    // Always only viewing due invoices here
    filter['due'] = '1';

    return filter;
  }

  /**
   * Given the query params, set the internal private state on page load.
   *
   * @param {Object} params
   */
  updateStateFromParams(params) {
    const getCheckboxStatus = (paramValue) => {
      if (paramValue === '1') {
        return LSG_CHECKBOX_TERNARY__STATES.ON;
      } else if (paramValue === '0') {
        return LSG_CHECKBOX_TERNARY__STATES.OFF;
      }

      return LSG_CHECKBOX_TERNARY__STATES.INDETERMINATE;
    };

    if (params.approved) {
      this.stateManager.updateTernaryStatus(getCheckboxStatus(params.approved), 'paramApproved');
    }

    if (params.agreement) {
      this.stateManager.updateTernaryStatus(getCheckboxStatus(params.agreement), 'paramAgreement');
    }

    if (params.batch) {
      this.set('_selectedBatchFilter', params.batch);
    } else {
      this.set('_selectedBatchFilter', this.state.BATCH_ALL);
    }

    if (params.applicationId) {
      this.set('_applicationId', params.applicationId);
      this.set('_selectedSearchCategory', 'applicationId');
    }

    if (params.id) {
      this.set('_id', params.id);
      this.set('_selectedSearchCategory', 'id');
    }

    if (params.companyId) {
      this.set('_companyId', params.companyId);
    }

    if (params.sort) {
      this.stateManager.updateStateFromSortParam(params.sort);
    }

    if (params.final_status) {
      this.stateManager.updateTernaryStatus(getCheckboxStatus(params.final_status), 'paramFinalStatus');
    }

    if (params.sent_status) {
      this.stateManager.updateTernaryStatus(getCheckboxStatus(params.sent_status), 'paramSentStatus');
    }

    if (params.unpaid) {
      this.stateManager.updateTernaryStatus(getCheckboxStatus(params.unpaid), 'paramUnpaid');
    }

    if (params.overpaid) {
      this.stateManager.updateTernaryStatus(getCheckboxStatus(params.overpaid), 'paramOverpaid');
    }

    if (params.exact) {
      this.stateManager.updateTernaryStatus(getCheckboxStatus(params.exact), 'paramExact');
    }

    if (params.partial) {
      this.stateManager.updateTernaryStatus(getCheckboxStatus(params.partial), 'paramPartial');
    }

    if (params.stripe) {
      this.stateManager.updateTernaryStatus(getCheckboxStatus(params.stripe), 'paramStripe');
    }

    if (params.invoice) {
      this.stateManager.updateTernaryStatus(getCheckboxStatus(params.invoice), 'paramInvoice');
    }

    if (params.revised_status) {
      this.stateManager.updateTernaryStatus(getCheckboxStatus(params.revised_status), 'paramRevisedStatus');
    }
  }

  /**
   * Given a param name, get the checkbox status for the template.
   *
   * @param {String} value      state value
   * @return {string}               LSG_CHECKBOX_TERNARY__STATES.*
   * @private
   */
  _getCheckboxStatusForValue(value) {
    if (value === '1') {
      return LSG_CHECKBOX_TERNARY__STATES.ON;
    } else if (value === '0') {
      return LSG_CHECKBOX_TERNARY__STATES.OFF;
    }

    return LSG_CHECKBOX_TERNARY__STATES.INDETERMINATE;
  }

  /**
   * Get the cost code string for the CSV downloader.
   *
   * @param {ShInvoice} shInvoice
   * @return {string}
   * @private
   */
  _getCostCodeStringForCSV(shInvoice) {
    let costCodes = [];
    if (!shInvoice.childShInvoices.length) {
      let lineItemWithApplication = shInvoice.shInvoiceLineItems.find(shInvoiceLineItem => {
        return shInvoiceLineItem.application.id;
      });

      return lineItemWithApplication?.application?.costCode?.body ?? '';
    }

    shInvoice.childShInvoices.forEach(childShInvoice => {
      let lineItemWithApplication = childShInvoice.shInvoiceLineItems.find(childShInvoiceLineItem => {
        return childShInvoiceLineItem.application.id;
      });

      const costCodeBody = lineItemWithApplication?.application?.costCode?.body;
      if (costCodeBody) {
        costCodes.push(costCodeBody);
      }
    });

    return costCodes.join(' || ');
  }

  /**
   * Get the total invoice amount for the CSV downloader.
   *
   * @param {ShInvoice} shInvoice
   * @return {String}
   * @private
   */
  _getInvoiceAmountForCSV(shInvoice) {
    if (shInvoice.isBatchedInvoice) {
      return shInvoice.amount;
    }

    return shInvoice.shInvoiceLineItems.reduce((carry, shInvoiceLineItem) => {
      return carry + shInvoiceLineItem.amount;
    }, 0);
  }

  @action
  clearFilters() {
    this.set('showFilterCard', false);
    this.state.init();
    this.stateManager.navigateToInvoicesPage(1);
  }

  @action
  filterNeedsApproval() {
    this.set('showFilterCard', false);
    this.set('state.paramApproved', '0');
    this.stateManager.navigateToInvoicesPage(1);
  }

  /**
   * @param {String} applicationId
   */
  @action
  onInputApplicationId(applicationId) {
    this.set('_applicationId', applicationId);
  }

  /**
   * @param {String} invoiceId
   */
  @action
  onInputInvoiceId(invoiceId) {
    this.set('_id', invoiceId);
  }

  /**
   * Called when the admin selects a company from the dropdown
   *
   * @param {Company} company
   */
  @action
  onCompanyEntitySelected(company) {
    this.set('_companyId', company.id);
  }

  /**
   * Format everything for the CSV downloader and trigger download.
   */
  @action
  onDownloadCSVClicked() {
    const delimiter = ',',
      momentString = this.moment.moment().format('MM_DD_YYYY'),
      fileName = `payin_invoices_${momentString}.csv`,
      columnHeaderRow = PAYIN__COLUMN_HEADERS.join(delimiter),
      invoices = this.state.shInvoices,
      moment = this.moment;

    let rows = invoices.map(invoice => {
      let invoiceItems = [];
      invoiceItems.push(invoice.id);

      let childInvoicesString = 'N/A';
      if (invoice.childShInvoices.length) {
        childInvoicesString = invoice.childShInvoices.map((childInvoice) => {
          return `${childInvoice.id}`;
        }).join(' ');
      }

      invoiceItems.push(childInvoicesString);
      invoiceItems.push(invoice.company.name);
      invoiceItems.push(this._getCostCodeStringForCSV(invoice));
      invoiceItems.push(invoice.description);
      invoiceItems.push(invoice.application.id);
      invoiceItems.push(makeMoney([this._getInvoiceAmountForCSV(invoice)], { commas: false }));
      invoiceItems.push(invoice.dueAt ? moment.moment(invoice.dueAt).format('YYYY-MM-DD kk:mm:ss') : '');
      invoiceItems.push(invoice.finalAt ? moment.moment(invoice.finalAt).format('YYYY-MM-DD kk:mm:ss') : '');
      invoiceItems.push(invoice.revisedAt ? moment.moment(invoice.revisedAt).format('YYYY-MM-DD kk:mm:ss') : '');
      invoiceItems.push(invoice.statusPaidText);
      invoiceItems.push(invoice.sentAt ? moment.moment(invoice.sentAt).format('YYYY-MM-DD kk:mm:ss') : '');
      return invoiceItems.join(delimiter);
    });

    const csvText = [columnHeaderRow].concat(rows).join('\n');

    FileSaver.saveAs(new Blob([csvText], { type: 'text/plain;charset=utf-8' }), fileName);
  }

  @action
  searchForResource(type, filter, callback) {
    let filterData = {};

    if (filter) {
      for (const key in filter) {
        // skip loop if the property is from prototype
        if (!filter.hasOwnProperty(key)) {
          continue;
        }
        filterData[key] = filter[key];
      }
    }

    filterData = {
      filter: filterData
    };

    return this.store.query(type, filterData).then(models => {
      if (typeof callback === 'function') {
        callback(models);
      }
      return models;
    });
  }

  /**
   *
   * @param {String} paramName      of state.param*
   * @param {String} checkboxStatus   LSG_CHECKBOX_TERNARY__STATES.*
   */
  @action
  onTernaryStatusChanged(paramName, checkboxStatus) {
    this.stateManager.updateTernaryStatus(checkboxStatus, paramName);
  }

  /**
   * Called by the filter form to do a new search with inputs.
   */
  @action
  searchInvoices() {
    this.set('showFilterCard', false);
    this.stateManager.navigateToInvoicesPage(1);
  }

  @action
  setBatchFilter(filter, e) {
    this.set('_selectedBatchFilter', filter);
  }

  /**
   * @param {String} category
   * @param e
   */
  @action
  setSearchCategory(category, e) {
    let currentSearchCategorySelected = this._selectedSearchCategory;

    switch (category) {
      case 'applicationId':
        if (currentSearchCategorySelected === category) {
          this.set('_selectedSearchCategory', '');
          this.set('_applicationId', null);
          return;
        }

        this.set('_id', null);
        this.set('_companyId', null);
        break;
      case 'id':
        if (currentSearchCategorySelected === category) {
          this.set('_selectedSearchCategory', '');
          this.set('_id', null);
          return;
        }

        this.set('_applicationId', null);
        this.set('_companyId', null);
        break;
      case 'entityName':
        if (currentSearchCategorySelected === category) {
          this.set('_selectedSearchCategory', '');
          this.set('_companyId', null);
          return;
        }

        this.set('_id', null);
        this.set('_applicationId', null);
        break;
    }


    this.set('_selectedSearchCategory', category);
  }

  @action
  toggleShowFilter() {
    this.toggleProperty('showFilterCard');
  }
}

export default AdminAccountingPayinController;
